import type { CSSProperties } from 'react'
import { useState, useCallback, useEffect } from 'react'
import { useDeviceInfo } from './useDeviceInfo'

interface ViewportScaleOptions {
  idealViewportWidth?: number
  enabled?: boolean
  disableOnTablet?: boolean
  transform: string | ((scale: number) => string)
  transformOrigin?: string
  baseLeft?: number
  baseRight?: number
}

export const useViewportScale = ({
  idealViewportWidth = 1200,
  enabled = true,
  disableOnTablet = false,
  transform,
  transformOrigin,
  baseLeft,
  baseRight
}: ViewportScaleOptions): CSSProperties => {
  const [scaleStyle, setScaleStyle] = useState<CSSProperties>({})
  const { isMobile, isTablet } = useDeviceInfo()

  const updateScaleStyle = useCallback(() => {
    if (!enabled || isMobile || (disableOnTablet && isTablet)) {
      setScaleStyle({})
      return
    }

    const viewportWidth = window.innerWidth
    const scaleFactor = Math.min(viewportWidth / idealViewportWidth, 1)

    const transformValue = typeof transform === 'function' ? transform(scaleFactor) : transform

    const style: CSSProperties = {
      transform: transformValue,
      // The factor, published for CSS. A scaled box's `vh` and `%` are measured
      // against the UNSCALED viewport and then shrunk with everything else, so
      // anything that wants to fill a share of the SCREEN has to divide by this
      // — e.g. `max-height: calc(92vh / var(--viewport-scale, 1))`. Inert for
      // every consumer that does not read it, and it keeps the factor in one
      // place instead of each surface re-deriving the formula above.
      ['--viewport-scale' as string]: String(scaleFactor)
    }

    if (transformOrigin) {
      style.transformOrigin = transformOrigin
    }

    if (baseLeft && typeof baseLeft === 'number') {
      style.left = `${baseLeft * scaleFactor}px`
    }

    if (baseRight && typeof baseRight === 'number') {
      style.right = `${baseRight * scaleFactor}px`
    }

    setScaleStyle(style)
  }, [
    enabled,
    isMobile,
    disableOnTablet,
    isTablet,
    idealViewportWidth,
    transform,
    transformOrigin,
    baseLeft,
    baseRight
  ])

  useEffect(() => {
    updateScaleStyle()
    window.addEventListener('resize', updateScaleStyle)
    return () => {
      window.removeEventListener('resize', updateScaleStyle)
    }
  }, [updateScaleStyle])

  return scaleStyle
}

// Preset transforms
export const transformPresets = {
  basic: (scale: number) => `scale(${scale})`,
  overlayBanners: (scale: number) => `scale(${scale}) translate(-50%, -50%)`,
  bottomPanel: (scale: number) => `scale(${scale}) translateX(-50%)`,
  rightFloatingPanel: (scale: number) => `scale(${scale}) translateY(-50%)`,
  streamMessage: (scale: number) => `scale(${scale}) translate(-50%, -50%)`
} as const
